import React, { useState } from 'react';
import { Button, TextField, Card, CardContent, Typography, CircularProgress, Alert } from '@mui/material';
import './otp-auth.css';

function OtpAuth({ onVerificationSuccess, user }) {
    const [step, setStep] = useState('request'); // 'request' or 'verify'
    const [otpCode, setOtpCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const PREDEFINED_EMAIL = 'maria.casaverde@impactivo.com';

    // Request OTP code
    const requestOtp = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const headers = {
                'Content-Type': 'application/json',
            };
            
            // Add authorization header if user is available
            if (user && user.tokenType && user.accessToken) {
                headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
            }

            const response = await fetch('/server/rest/otp/send', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ email: PREDEFINED_EMAIL }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setStep('verify');
                setSuccessMessage('OTP sent successfully! Please check your email for the code.');
            } else {
                setError(data.message || 'Failed to send OTP. Please try again.');
            }
        } catch (err) {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Verify OTP code
    const verifyOtp = async () => {
        if (!otpCode.trim()) {
            setError('Please enter the OTP code');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const headers = {
                'Content-Type': 'application/json',
            };
            
            // Add authorization header if user is available
            if (user && user.tokenType && user.accessToken) {
                headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
            }

            const response = await fetch('/server/rest/otp/verify', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ 
                    email: PREDEFINED_EMAIL,
                    otp: otpCode.trim()
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Store verification status in localStorage
                localStorage.setItem('emailAutomationOtpVerified', 'true');
                onVerificationSuccess();
            } else {
                setError(data.message || 'Invalid OTP code. Please try again.');
            }
        } catch (err) {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        if (step === 'request') {
            requestOtp();
        } else {
            verifyOtp();
        }
    };

    return (
        <div className="otp-auth-container">
            <Card className="otp-auth-card">
                <CardContent>
                    <Typography variant="h4" component="h1" gutterBottom align="center">
                        Email Automation Access
                    </Typography>
                    
                    {successMessage && (
                        <Alert severity="success" style={{ marginBottom: '20px' }}>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert severity="error" style={{ marginBottom: '20px' }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        {step === 'request' ? (
                            <div>
                                <Typography variant="body1" paragraph align="center">
                                    To access the email automation features, please verify your identity by requesting an OTP code.
                                </Typography>
                                <Typography variant="body1" paragraph align="center">
                                    An OTP code will be sent to: <strong>{PREDEFINED_EMAIL}</strong>
                                </Typography>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={loading}
                                    size="large"
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Request OTP'}
                                </Button>
                            </div>
                        ) : (
                            <div>
                                <Typography variant="body1" paragraph align="center">
                                    Please enter the OTP code you received in your email:
                                </Typography>
                                <TextField
                                    fullWidth
                                    label="OTP Code"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value)}
                                    margin="normal"
                                    variant="outlined"
                                    inputProps={{ maxLength: 6 }}
                                    autoFocus
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={loading}
                                    size="large"
                                    style={{ marginTop: '20px' }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Verify OTP'}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setStep('request');
                                        setOtpCode('');
                                    }}
                                    fullWidth
                                    style={{ marginTop: '10px' }}
                                >
                                    Resend OTP
                                </Button>
                            </div>
                        )}
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default OtpAuth;
